import React, { useState, useEffect } from 'react';
import { Lock, Radio, KeyRound, ArrowRight, Mail, Smartphone, Shield } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

export default function LoginPage({ onLogin }) {
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [config, setConfig] = useState({
    googleEnabled: false,
    emailOtpEnabled: false,
    totpEnabled: false,
    methods: [],
    otpEmailHint: '',
  });
  const [step, setStep] = useState('password'); // 'password' | '2fa' | 'code'
  const [loginToken, setLoginToken] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [chosenMethod, setChosenMethod] = useState(''); // 'email' | 'totp'

  useEffect(() => {
    api.get('/auth/config').then(r => setConfig(r.data)).catch(() => {});
  }, []);

  const finishLogin = (token) => {
    localStorage.setItem('auth_token', token);
    toast.success('התחברת בהצלחה!');
    onLogin();
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!password) {
      toast.error('נא להזין סיסמה');
      return;
    }
    setIsLoading(true);
    try {
      const response = await api.post('/auth/login', { password });
      const data = response.data;

      if (data.step === '2fa') {
        setLoginToken(data.loginToken);
        if (data.autoSent === 'email') {
          setChosenMethod('email');
          setStep('code');
          toast.success('נשלח קוד אימות למייל');
        } else if (data.methods.length === 1 && data.methods[0] === 'totp') {
          setChosenMethod('totp');
          setStep('code');
          toast('נא להזין קוד מאפליקציית האימות');
        } else {
          setStep('2fa');
        }
      } else if (data.success && data.token) {
        finishLogin(data.token);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'שגיאה בהתחברות');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChooseMethod = async (method) => {
    setChosenMethod(method);
    if (method === 'email') {
      setIsLoading(true);
      try {
        await api.post('/auth/send-otp', { loginToken });
        toast.success('נשלח קוד אימות למייל');
      } catch (error) {
        toast.error(error.response?.data?.error || 'שגיאה בשליחת הקוד');
      } finally {
        setIsLoading(false);
      }
    }
    setStep('code');
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!otpCode) {
      toast.error('נא להזין את הקוד');
      return;
    }
    setIsLoading(true);
    try {
      const response = await api.post('/auth/verify-otp', {
        loginToken,
        code: otpCode,
        method: chosenMethod,
      });
      if (response.data.success) finishLogin(response.data.token);
    } catch (error) {
      toast.error(error.response?.data?.error || 'קוד שגוי');
    } finally {
      setIsLoading(false);
    }
  };

  const resetToPassword = () => {
    setStep('password');
    setOtpCode('');
    setChosenMethod('');
  };

  const loginWithGoogle = () => {
    window.location.href = '/api/auth/sso/google';
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full mb-4">
              <Radio className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              מערכת ניהול חדשות
            </h1>
            <p className="text-gray-600">
              מעקב אחר ערוצי טלגרם בזמן אמת
            </p>
          </div>

          {/* Google login */}
          {config.googleEnabled && step === 'password' && (
            <>
              <button
                type="button"
                onClick={loginWithGoogle}
                className="w-full flex justify-center items-center gap-2 py-3 px-4 border border-gray-300 rounded-lg shadow-sm text-gray-700 bg-white hover:bg-gray-50 transition-all font-medium mb-4"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.46 14.97.5 12 .5A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.3 9.14 4.75 12 4.75z"/></svg>
                כניסה עם Google
              </button>
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-xs text-gray-400">או עם סיסמה</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>
            </>
          )}

          {/* Password step */}
          {step === 'password' && (
            <form onSubmit={handlePasswordSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  סיסמה
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="block w-full pr-10 pl-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="הזן סיסמה"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white ml-2"></div>
                    מתחבר...
                  </>
                ) : (
                  'כניסה למערכת'
                )}
              </button>
            </form>
          )}

          {/* 2FA method selection */}
          {step === '2fa' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 text-center mb-4">
                נא לבחור שיטת אימות דו-שלבי
              </p>

              {config.methods.includes('email') && (
                <button
                  type="button"
                  onClick={() => handleChooseMethod('email')}
                  disabled={isLoading}
                  className="w-full flex items-center gap-3 p-4 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all text-right"
                >
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">קוד במייל</p>
                    <p className="text-sm text-gray-500">נשלח קוד בן 6 ספרות למייל {config.otpEmailHint}</p>
                  </div>
                </button>
              )}

              {config.methods.includes('totp') && (
                <button
                  type="button"
                  onClick={() => handleChooseMethod('totp')}
                  disabled={isLoading}
                  className="w-full flex items-center gap-3 p-4 border-2 border-gray-200 rounded-xl hover:border-green-500 hover:bg-green-50 transition-all text-right"
                >
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Smartphone className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">אפליקציית אימות</p>
                    <p className="text-sm text-gray-500">Google Authenticator או דומה</p>
                  </div>
                </button>
              )}

              <button
                type="button"
                onClick={resetToPassword}
                className="w-full flex justify-center items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mt-2"
              >
                <ArrowRight className="w-4 h-4" /> חזרה לסיסמה
              </button>
            </div>
          )}

          {/* Code input step */}
          {step === 'code' && (
            <form onSubmit={handleVerifyCode} className="space-y-6">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mb-3">
                  {chosenMethod === 'email' ? (
                    <Mail className="w-6 h-6 text-blue-600" />
                  ) : (
                    <Shield className="w-6 h-6 text-green-600" />
                  )}
                </div>
                <p className="text-sm text-gray-600">
                  {chosenMethod === 'email'
                    ? `נשלח קוד אימות בן 6 ספרות למייל ${config.otpEmailHint}`
                    : 'הזן את הקוד מיישומון האימות (Google Authenticator)'
                  }
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {chosenMethod === 'email' ? 'קוד אימות' : 'קוד TOTP'}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <KeyRound className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                    className="block w-full pr-10 pl-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-center tracking-[0.5em] text-lg font-mono"
                    placeholder="------"
                    autoFocus
                    disabled={isLoading}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center items-center py-3 px-4 rounded-lg shadow-sm text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 transition-all font-medium"
              >
                {isLoading ? 'מאמת...' : 'אישור קוד'}
              </button>

              {chosenMethod === 'email' && (
                <button
                  type="button"
                  onClick={() => handleChooseMethod('email')}
                  disabled={isLoading}
                  className="w-full text-sm text-blue-600 hover:text-blue-800"
                >
                  שלח קוד מחדש
                </button>
              )}

              <button
                type="button"
                onClick={resetToPassword}
                className="w-full flex justify-center items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
              >
                <ArrowRight className="w-4 h-4" /> חזרה
              </button>
            </form>
          )}

          {/* Footer */}
          <div className="mt-6 text-center text-sm text-gray-500">
            <p>מערכת מאובטחת לניהול תוכן</p>
          </div>
        </div>
      </div>
    </div>
  );
}
